import {
    mat4,
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec3,
    vec3,
} from 'gl-matrix';
import { getDominantAxisIndex } from '~/utils/units';

/**
 * A volume's voxel counts and in-plane spacing — the whole of what a
 * function measuring within one needs.
 *
 * Structural, and deliberately not the engine's `Grid` class: a function
 * that takes only this can be exercised on a plain object, and cannot
 * reach for a grid fact it did not declare.
 */
export interface GridDimensions {
    sizeX: number;
    sizeY: number;
    sizeZ: number;
    /** in-plane spacing in mm */
    resX: number;
    resY: number;
}

/**
 * THE frame every couch-top module shares, stated once here so the
 * per-function docs below need not restate it.
 *
 * A couch-top placement is a continuous **voxel Y** in the image's own
 * grid, corner convention: `Grid.voxelToWorld` puts integer indices at
 * voxel corners, so row `j`'s centre sits at `j + 0.5` and the volume's
 * lateral centre is `sizeX / 2`, not `(sizeX - 1) / 2`.
 *
 * Voxel Y is the placement's own invariant, and the reason nothing here
 * reduces it to a patient-frame scalar: the couch depth direction is a
 * direction in the IMAGE frame, and which PATIENT axis carries it follows
 * the series' direction cosines — patient Y (anterior-posterior) for the
 * head/feet-first supine and prone orientations, patient X (left-right)
 * for lateral decubitus. A hardcoded "world Y" is therefore wrong on
 * exactly the decubitus series that pass every other gate. Placement
 * resolves each vertex in voxel space and transforms once through
 * `voxelToWorld`, which is correct by construction for any axis-aligned
 * orientation; {@link imageRowWorldAxis} exists only to LABEL a readout.
 */

/**
 * Patient-frame point on the couch-top line at `couchTopVoxelY`: the
 * volume's lateral centre at that depth, on the slice `voxelZ`.
 *
 * Returns the whole point, not one component — see the frame note above.
 * It is the exact pre-image of {@link worldPointToCouchTopVoxelY} for
 * every invertible `voxelToWorld`, oblique included.
 *
 * `voxelZ` is EXPLICIT and REQUIRED, not a hidden 0: for an oblique series
 * `voxelToWorld` rotates the depth direction as a function of voxel Z too,
 * so passing the wrong Z is silent and wrong by exactly that rotation.
 * Callers pass the voxel Z of the plane the line is drawn against (the
 * axial view's current slice), which is what makes a drag → redraw
 * round-trip idempotent.
 */
export function couchTopWorldPoint(
    couchTopVoxelY: number,
    grid: GridDimensions,
    voxelToWorld: ReadonlyMat4,
    voxelZ: number
): vec3 {
    return vec3.transformMat4(
        vec3.create(),
        vec3.fromValues(grid.sizeX / 2, couchTopVoxelY, voxelZ),
        voxelToWorld
    );
}

/**
 * The patient-frame axis (0 = X, 1 = Y, 2 = Z) the image ROW index
 * advances along, from `voxelToWorld`'s second column.
 *
 * For an oblique matrix the row direction lies between axes and this
 * returns the DOMINANT one only — an approximation for display, which is
 * all it is used for. Throws on a degenerate (zero-length) row direction
 * rather than defaulting to an axis.
 */
export function imageRowWorldAxis(voxelToWorld: ReadonlyMat4): 0 | 1 | 2 {
    return getDominantAxisIndex([
        voxelToWorld[4],
        voxelToWorld[5],
        voxelToWorld[6],
    ]);
}

/**
 * The same axis, read from a bare 3x3 orientation rather than a full
 * voxel-to-world transform, for callers that hold only the direction
 * cosines.
 */
export function imageRowPatientAxis(orientation: ReadonlyMat3): 0 | 1 | 2 {
    return getDominantAxisIndex([
        orientation[3],
        orientation[4],
        orientation[5],
    ]);
}

/**
 * The single patient-frame coordinate a numeric readout can show for a
 * couch top: {@link couchTopWorldPoint} reduced to the axis the row runs
 * along ({@link imageRowWorldAxis}).
 *
 * A caller displaying this number must label it from that same axis, not
 * from a fixed "Y". Exact only when the row direction is parallel to a
 * patient axis; on an oblique series it is display-only (Apply refuses
 * oblique grids outright — see `placeCouchModel`).
 */
export function couchTopWorldCoord(
    couchTopVoxelY: number,
    grid: GridDimensions,
    voxelToWorld: ReadonlyMat4,
    voxelZ: number
): number {
    const p = couchTopWorldPoint(couchTopVoxelY, grid, voxelToWorld, voxelZ);
    return p[imageRowWorldAxis(voxelToWorld)];
}

/**
 * The couch-top voxel Y whose patient-frame position is `p` (a full 3-D
 * point, e.g. from `PlaneView.clientToPatientCoordinates`).
 *
 * Takes a whole world point rather than a bare Y, and needs no grid:
 * transforming the point through the inverse of `voxelToWorld` recovers
 * voxel Y — the depth axis BY DEFINITION, whatever patient axis it landed
 * on — and is exact for any rotation.
 *
 * Throws if `voxelToWorld` is not invertible rather than silently
 * returning a wrong depth.
 */
export function worldPointToCouchTopVoxelY(
    p: ReadonlyVec3,
    voxelToWorld: ReadonlyMat4
): number {
    const worldToVoxel = mat4.create();
    if (!mat4.invert(worldToVoxel, voxelToWorld)) {
        throw new Error(
            'worldPointToCouchTopVoxelY: voxelToWorld is not invertible'
        );
    }
    return vec3.transformMat4(vec3.create(), p, worldToVoxel)[1];
}
